import React, { useCallback, useEffect, useState } from 'react';

import { apiBaseUrl } from './storage';

interface AdminPlayerRow {
  id: string;
  username: string;
  createdAt: number;
  lastSeenAt: number;
  lastSeenIp: string | null;
  gamesPlayed: number;
  gamesWon: number;
  currentStreak: number;
  longestStreak: number;
  totalXp: number;
  level: number;
}

const SESSION_KEY = 'minesweeper.admin.token.v1';

function formatDate(ms: number): string {
  return new Date(ms).toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function recencyLabel(lastSeenAt: number, now: number): string {
  const minutes = (now - lastSeenAt) / 60_000;
  if (minutes < 5) return 'Playing now';
  if (minutes < 60) return `${Math.round(minutes)}m ago`;
  const hours = minutes / 60;
  if (hours < 24) return `${Math.round(hours)}h ago`;
  const days = hours / 24;
  if (days < 30) return `${Math.round(days)}d ago`;
  return formatDate(lastSeenAt);
}

function winRate(row: AdminPlayerRow): string {
  return row.gamesPlayed === 0 ? '—' : `${Math.round((row.gamesWon / row.gamesPlayed) * 100)}%`;
}

const AVATAR_COLORS = ['#0a84ff', '#30d158', '#ff9f0a', '#ff453a', '#bf5af2', '#64d2ff', '#ff375f'];

/** Deterministic colour per username, so the same player always gets the same avatar. */
function avatarColor(username: string): string {
  let hash = 0;
  for (let i = 0; i < username.length; i++) hash = (hash * 31 + username.charCodeAt(i)) | 0;
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

function isOnlineNow(lastSeenAt: number, now: number): boolean {
  return now - lastSeenAt < 5 * 60_000;
}

/**
 * A minimal, functional admin view — not part of the game's own navigation.
 * Reached by going to /admin directly. Same visual language (dark panels,
 * the existing .btn/.panel classes) so it doesn't need its own stylesheet,
 * but deliberately plain: this is an operator tool, not a product surface.
 */
export function AdminApp(): React.ReactElement {
  const apiUrl = apiBaseUrl();
  const [token, setToken] = useState<string | null>(() => {
    try {
      return sessionStorage.getItem(SESSION_KEY);
    } catch {
      return null;
    }
  });
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Drives the "Playing now" / "3m ago" recency labels — refreshed on an
  // interval rather than computed once, so a page left open doesn't drift
  // into showing stale relative times.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 30_000);
    return () => window.clearInterval(timer);
  }, []);

  const [players, setPlayers] = useState<AdminPlayerRow[]>([]);
  const [cursor, setCursor] = useState<number | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const signOut = useCallback(() => {
    setToken(null);
    setPlayers([]);
    setCursor(null);
    setHasMore(true);
    try {
      sessionStorage.removeItem(SESSION_KEY);
    } catch {
      // Nothing to clean up if storage isn't available.
    }
  }, []);

  const loadPage = useCallback(
    async (activeToken: string, before: number | null) => {
      setLoading(true);
      setLoadError(null);
      try {
        const params = new URLSearchParams({ limit: '50' });
        if (before !== null) params.set('before', String(before));
        const res = await fetch(`${apiUrl}/v1/admin/players?${params.toString()}`, {
          headers: { Authorization: `Bearer ${activeToken}` },
        });
        if (res.status === 401) {
          signOut();
          return;
        }
        if (!res.ok) {
          const body = await res.json().catch(() => null);
          throw new Error(body?.message ?? `Request failed (${res.status}).`);
        }
        const data = (await res.json()) as { players: AdminPlayerRow[]; nextCursor: number | null };
        setPlayers((existing) => (before === null ? data.players : [...existing, ...data.players]));
        setCursor(data.nextCursor);
        setHasMore(data.nextCursor !== null);
      } catch (error) {
        setLoadError(error instanceof Error ? error.message : 'Could not load players.');
      } finally {
        setLoading(false);
      }
    },
    [apiUrl, signOut],
  );

  useEffect(() => {
    if (token) void loadPage(token, null);
  }, [token, loadPage]);

  const deletePlayer = async (player: AdminPlayerRow): Promise<void> => {
    if (!token) return;
    // Permanent and irreversible: the row, their games, and their scores are
    // all gone server-side (see admin.ts's `deletePlayer`) — worth a pause
    // before firing it.
    const confirmed = window.confirm(
      `Permanently delete "${player.username}"? This removes their account and every game and score on record. This cannot be undone.`,
    );
    if (!confirmed) return;
    setLoadError(null);
    try {
      const res = await fetch(
        `${apiUrl}/v1/admin/players/${encodeURIComponent(player.id)}/delete`,
        { method: 'POST', headers: { Authorization: `Bearer ${token}` } },
      );
      if (res.status === 401) {
        signOut();
        return;
      }
      if (!res.ok) {
        // A delete that quietly does nothing is worse than one that fails
        // loudly: the row stayed on screen and there was no way to tell
        // whether the account was gone or the request had been refused.
        const body = await res.json().catch(() => null);
        throw new Error(body?.message ?? `Delete failed (${res.status}).`);
      }
      setPlayers((list) => list.filter((p) => p.id !== player.id));
    } catch (error) {
      setLoadError(error instanceof Error ? error.message : 'Could not delete that player.');
    }
  };

  if (!token) {
    return (
      <div className="admin-shell">
        <form
          className="panel admin-login"
          onSubmit={(event) => {
            event.preventDefault();
            if (!password || busy) return;
            setBusy(true);
            setLoginError(null);
            fetch(`${apiUrl}/v1/admin/login`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ password }),
            })
              .then(async (res) => {
                if (!res.ok) {
                  const body = await res.json().catch(() => null);
                  throw new Error(body?.message ?? 'Login failed.');
                }
                return res.json() as Promise<{ token: string }>;
              })
              .then((data) => {
                try {
                  sessionStorage.setItem(SESSION_KEY, data.token);
                } catch {
                  // Session just won't survive a reload; still usable this tab.
                }
                setToken(data.token);
                setPassword('');
              })
              .catch((error: unknown) => {
                setLoginError(error instanceof Error ? error.message : 'Login failed.');
              })
              .finally(() => setBusy(false));
          }}
        >
          <h1 className="panel-title">Admin</h1>
          <input
            type="password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            placeholder="Admin password"
            autoComplete="current-password"
            autoFocus
          />
          <button type="submit" className="btn" disabled={busy || !password}>
            {busy ? 'Signing in…' : 'Sign in'}
          </button>
          {loginError ? <p className="error small">{loginError}</p> : null}
        </form>
      </div>
    );
  }

  const onlineCount = players.filter((p) => isOnlineNow(p.lastSeenAt, now)).length;

  return (
    <div className="admin-shell">
      <header className="admin-header">
        <button type="button" className="btn ghost" onClick={signOut}>
          Sign out
        </button>
      </header>

      {loadError ? <p className="error small">{loadError}</p> : null}

      <div className="admin-stats">
        <div className="admin-stat">
          <span className="admin-stat-value">{players.length}</span>
          <span className="admin-stat-label">Loaded{hasMore ? ' (more available)' : ''}</span>
        </div>
        <div className="admin-stat">
          <span className="admin-stat-value">{onlineCount}</span>
          <span className="admin-stat-label">Playing now</span>
        </div>
      </div>

      <p className="faint small">
        Deleting a player is permanent — it removes their account and every game and score they
        have on record. There is no undo.
      </p>

      <div className="panel admin-table-wrap">
        <table className="admin-table">
          <thead>
            <tr>
              <th>Player</th>
              <th title="Internal player ID">ID</th>
              <th>Joined</th>
              <th>Last active</th>
              <th title="IP address the player was last seen from">Last IP</th>
              <th className="numeric">Level</th>
              <th className="numeric">XP</th>
              <th className="numeric">Played</th>
              <th className="numeric">Won</th>
              <th className="numeric" title="Games won divided by games played">
                Win %
              </th>
              <th className="numeric">Streak</th>
              <th className="numeric">Best streak</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {players.map((p) => {
              const online = isOnlineNow(p.lastSeenAt, now);
              return (
                <tr key={p.id}>
                  <td>
                    <div className="admin-player">
                      <span className="admin-avatar" style={{ background: avatarColor(p.username) }}>
                        {p.username.slice(0, 1)}
                      </span>
                      <span className="admin-username">{p.username}</span>
                    </div>
                  </td>
                  <td className="admin-id" title={p.id}>
                    {p.id.slice(0, 8)}
                  </td>
                  <td>{formatDate(p.createdAt)}</td>
                  <td title={formatDate(p.lastSeenAt)}>
                    {online ? (
                      <span className="admin-pill online">Playing now</span>
                    ) : (
                      recencyLabel(p.lastSeenAt, now)
                    )}
                  </td>
                  <td>{p.lastSeenIp ?? '—'}</td>
                  <td className="numeric">{p.level}</td>
                  <td className="numeric">{p.totalXp}</td>
                  <td className="numeric">{p.gamesPlayed}</td>
                  <td className="numeric">{p.gamesWon}</td>
                  <td className="numeric">{winRate(p)}</td>
                  <td className="numeric">{p.currentStreak}</td>
                  <td className="numeric">{p.longestStreak}</td>
                  <td>
                    <button type="button" className="btn ghost danger" onClick={() => void deletePlayer(p)}>
                      Delete
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {players.length === 0 && !loading ? <p className="faint small">No players yet.</p> : null}
      </div>

      {hasMore ? (
        <button
          type="button"
          className="btn ghost"
          disabled={loading}
          onClick={() => token && void loadPage(token, cursor)}
        >
          {loading ? 'Loading…' : 'Load more'}
        </button>
      ) : null}
    </div>
  );
}
